/**
 * Pick a board — clean replacement for /profiles in the new top-nav world.
 *
 * Single job: let the parent pick which child's board they want to work
 * on. Selecting a board stores it in localStorage so every other top-nav
 * tab knows the context. The old Profiles page kept Refer-a-Friend, Plan
 * chips, and pilot CTAs jammed in here; this page strips them all out and
 * routes those concerns to My Profile.
 *
 * Also shows a "Pick a board to edit first" banner when the user got
 * here by clicking a board-scoped tab without a selection.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Plus, ChevronRight, AlertTriangle } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useSEO } from "@/lib/seo";
import { setSelectedBoardId } from "@/lib/selectedBoard";
import { PICK_REDIRECT_KEY } from "@/components/TopNav";

export default function PickBoard() {
  useSEO({
    title: "Pick a board",
    description: "Choose which child's board to edit.",
    path: "/pick-board",
  });
  const nav = useNavigate();
  const [params] = useSearchParams();
  const intent = params.get("intent") || "";
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [redirectTab, setRedirectTab] = useState("");

  useEffect(() => {
    try {
      // Prefer explicit ?intent=... on the URL; fall back to the TopNav's
      // sessionStorage stash. AccountMenu → Parental Settings uses the URL
      // path; TopNav tab clicks use sessionStorage.
      const t = intent || sessionStorage.getItem(PICK_REDIRECT_KEY) || "";
      if (t) {
        setRedirectTab(t);
        sessionStorage.removeItem(PICK_REDIRECT_KEY);
      }
    } catch { /* noop */ }
    api.get("/profiles")
      .then((r) => {
        const list = r.data?.profiles || r.data || [];
        setBoards(list);
        // Defect #6 (2026-07-04): if the parent has exactly ONE board and
        // came here via an intent link (Parental Settings, Manage Board,
        // etc.), skip the pick step and drop them into the relevant tab.
        // No more "dead door" feeling for judges.
        const targetIntent = intent || sessionStorage.getItem(PICK_REDIRECT_KEY) || "";
        if (list.length === 1 && targetIntent) {
          setSelectedBoardId(list[0].id);
          nav(`/manage/${list[0].id}?tab=${targetIntent}`, { replace: true });
        }
      })
      .catch((e) => setError(formatApiErrorDetail(e?.response?.data?.detail) || "Could not load boards."))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intent]);

  const pick = (board, target) => {
    setSelectedBoardId(board.id);
    if (target === "edit") {
      nav(`/manage/${board.id}?tab=create-button`);
    } else {
      nav(`/board/${board.id}`);
    }
  };

  const REDIRECT_LABEL = {
    "create-button": "Create a Button",
    colors: "Colors & Backgrounds",
    modes: "Modes",
    "manage-board": "Manage Board",
    parental: "Parental Settings",
  };

  return (
    <div className="min-h-screen bg-[color:var(--cream)] px-4 sm:px-6 py-6">
      <div className="max-w-4xl mx-auto">
        <h1 className="display text-2xl sm:text-3xl font-extrabold mb-2" data-testid="pick-board-title">
          Pick a board
        </h1>
        <p className="text-sm text-[color:var(--ink)]/70 mb-5">
          Pick one of your child's boards to open or edit.
        </p>

        {redirectTab && REDIRECT_LABEL[redirectTab] && (
          <div
            className="brutal-card bg-amber-50 p-3 text-sm flex items-center gap-2 mb-4 text-amber-900"
            data-testid="pick-board-redirect-banner"
          >
            <AlertTriangle size={16} className="shrink-0" />
            <span>
              Pick a board first to open <strong>{REDIRECT_LABEL[redirectTab]}</strong>.
            </span>
          </div>
        )}

        {error && (
          <div className="brutal-card bg-red-50 p-3 text-sm text-red-700 mb-4" data-testid="pick-board-error">
            {error}
          </div>
        )}

        {loading && <div className="text-sm text-[color:var(--ink)]/60">Loading…</div>}

        {!loading && boards.length === 0 && (
          <div className="brutal-card bg-white p-6 text-center" data-testid="pick-board-empty">
            <p className="text-sm text-[color:var(--ink)]/70 mb-3">
              No boards yet. Create the first one — you'll add the words your
              child knows in a quick step-by-step wizard.
            </p>
            <Link to="/create-board" className="pill-btn primary" data-testid="pick-board-create-first">
              <Plus size={16} /> Create your first board
            </Link>
          </div>
        )}

        {!loading && boards.length > 0 && (
          <ul className="grid sm:grid-cols-2 gap-3" data-testid="pick-board-list">
            {boards.map((b) => (
              <li
                key={b.id}
                className="brutal-card bg-white p-4"
                data-testid={`pick-board-card-${b.id}`}
              >
                <div className="flex items-center gap-3 mb-3">
                  <span
                    className="w-12 h-12 rounded-2xl border-[3px] border-[color:var(--ink)] flex items-center justify-center text-2xl shrink-0"
                    style={{ background: b.color || "#FFB627" }}
                  >
                    {b.avatar || "🌟"}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="font-extrabold text-base truncate">{b.name}</div>
                    <div className="text-xs text-[color:var(--ink)]/60">
                      {b.board_level
                        ? b.board_level.replace(/^\w/, (c) => c.toUpperCase()).replace(/(\d+)/, " $1")
                        : "Custom board"}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => pick(b, "open")}
                    className="pill-btn primary flex-1 justify-center"
                    data-testid={`pick-board-open-${b.id}`}
                  >
                    Open
                  </button>
                  <button
                    type="button"
                    onClick={() => pick(b, "edit")}
                    className="pill-btn flex-1 justify-center"
                    data-testid={`pick-board-edit-${b.id}`}
                  >
                    Edit <ChevronRight size={14} />
                  </button>
                </div>
              </li>
            ))}
            <li className="brutal-card bg-[color:var(--gold)]/30 p-4 flex flex-col justify-center items-center text-center">
              <Plus size={28} className="mb-2" />
              <Link to="/create-board" className="pill-btn primary" data-testid="pick-board-create-another">
                Create another board
              </Link>
            </li>
          </ul>
        )}
      </div>
    </div>
  );
}
